import React, { useState, useRef, useEffect } from 'react'
import ReactTooltip from 'react-tooltip'
import Calendar from 'react-calendar'
import PropTypes from 'prop-types'
import { toZonedTime, fromZonedTime, format } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

// import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import Icon from '../../../Icons'
import API from '../../../../api/api'
import { isDayAlreadyReserved, getTimezonedDate, getReservedOnDateRange } from '../helpers'
import { getTimezoneStamp } from '../../../../helpers/helpers'

import TimeSelectView from './TimeSelectView'

const TimeBlockCalendar = ({
  amenity,
  building,
  locationId,
  timeSelectViewVisible,
  selectedTimeRange,
  setIsTimeSelectVisible = () => {},
  setSelectedTimeRange = () => {},
  setHasConflicts = () => {},
  onChange = () => {},
  setReservedTimeRanges = () => {},
}) => {
  const { t } = useTranslation(['client-reservations'])
  const {
    id,
    multiDayBooking,
    endDate,
    startDate,
    daysAvailable,
    managerialBooking,
    unavailableSlots,
  } = amenity

  const { timezone, id: buildingId } = building
  const timeout = useRef(null)
  const [showAllDates, setShowAllDates] = useState(false)
  const [amenityReservations, setAmenityReservations] = useState([])
  const [selectedStartEnd, setSelectedStartEnd] = useState([])
  const [blockFullDay, setBlockFullDay] = useState(false)
  const [blockFullDayDisabled, setBlockFullDayDisabled] = useState(false)
  const [blockMultipleDays, setBlockMultipleDays] = useState(false)

  const [selectedStartDate, selectedEndDate] = selectedStartEnd
  const filteredManagerialBooking = (managerialBooking || []).filter((booking) =>
    locationId ? booking?.locationId === locationId : true,
  )

  const actualTzTimestamp = timezone
    ? toZonedTime(new Date(), timezone).getTime()
    : new Date().getTime()

  const calendarValue =
    selectedStartEnd.length === 2
      ? selectedStartEnd
      : selectedStartEnd.length
        ? selectedStartEnd[0]
        : null

  const checkIsRangeHasReserved = async (amenityId, startDate, endDate) => {
    try {
      const data = await API.getReservationsInDateRange(
        buildingId,
        amenityId,
        getTimezoneStamp(new Date(startDate).getTime(), { timezone }),
        new Date(getTimezoneStamp(new Date(endDate).getTime(), { timezone })).setHours(
          23,
          59,
          0,
          0,
        ),
      )

      return data.length > 0
    } catch ({ message }) {
      console.error(message)
    }
    return false
  }

  const checkAvailability = async (selectedDays, firstDaySelected, lastDaySelected) => {
    const firstDayReserved = isDayAlreadyReserved(
      firstDaySelected,
      amenityReservations,
      actualTzTimestamp,
      timezone,
    )
    const lastDayReserved = isDayAlreadyReserved(
      new Date(lastDaySelected).setHours(0, 0, 0, 0),
      amenityReservations,
      actualTzTimestamp,
      timezone,
    )

    const hasDisabledDays = selectedDays.find((selectedDay) =>
      getDayDisabled(new Date(selectedDay).setHours(0, 0, 0, 0)),
    )
    const ongoingReservation = amenityReservations.find(
      (reservation) =>
        reservation.date <= actualTzTimestamp && reservation.endDate >= actualTzTimestamp,
    )

    const [matchedOngoing] = getReservedOnDateRange(
      firstDaySelected,
      new Date(lastDaySelected).setHours(0, 0, 0, 0),
      ongoingReservation ? [ongoingReservation] : [],
      timezone,
    )

    const daysBlockingConditions =
      lastDayReserved === 'endDay' ||
      (firstDayReserved === 'endDay' && lastDayReserved === 'endDay') ||
      firstDayReserved === 'startDay'

    let isAlreadyReserved =
      selectedDays.find(
        (selectedDay) =>
          isDayAlreadyReserved(
            new Date(selectedDay).setHours(0, 0, 0, 0),
            amenityReservations,
            actualTzTimestamp,
            timezone,
          ) === 'fullDay',
      ) ||
      daysBlockingConditions ||
      (lastDayReserved === null &&
        selectedDays
          .slice(1)
          .find(
            (selectedDay) =>
              isDayAlreadyReserved(
                new Date(selectedDay).setHours(0, 0, 0, 0),
                amenityReservations,
                actualTzTimestamp,
                timezone,
              ) === 'endDay',
          ))

    const timeBlocks = getReservedOnDateRange(
      firstDaySelected,
      new Date(lastDaySelected).setHours(0, 0, 0, 0),
      filteredManagerialBooking.map((booking) => ({
        date: booking.startDate,
        endDate: booking.endDate,
      })),
      timezone,
      true,
    )

    const hasTimeBlocks = timeBlocks.length > 0

    if (showAllDates || !multiDayBooking) {
      if (!multiDayBooking) {
        if (hasDisabledDays) {
          setSelectedStartEnd([])
          return
        }

        isAlreadyReserved = await checkIsRangeHasReserved(id, firstDaySelected, lastDaySelected)
      }

      if (matchedOngoing || hasTimeBlocks) {
        setSelectedStartEnd([])
        return
      }

      if (isAlreadyReserved) {
        setHasConflicts(true)
      } else {
        setHasConflicts(false)
      }
    } else if (isAlreadyReserved || matchedOngoing || hasDisabledDays || hasTimeBlocks) {
      setSelectedStartEnd([])
      return
    }

    if (
      (firstDayReserved || lastDayReserved) &&
      (!showAllDates || (showAllDates && timeBlocks.length > 0))
    ) {
      setBlockFullDayDisabled(true)
      setBlockFullDay(false)
    } else if (blockFullDayDisabled) {
      setBlockFullDayDisabled(false)
    }

    setSelectedStartEnd([firstDaySelected, lastDaySelected])
  }

  const setSelectedDays = (startEndCalendarClicked) => {
    if (!multiDayBooking && !blockMultipleDays) {
      setSelectedStartEnd([startEndCalendarClicked])
      setIsTimeSelectVisible(true)
    } else {
      const allDaysInRange = []
      allDaysInRange.push(startEndCalendarClicked[0])
      let nextDay = new Date(
        new Date(startEndCalendarClicked[0]).setDate(
          new Date(startEndCalendarClicked[0]).getDate() + 1,
        ),
      )
      while (
        format(nextDay, 'yyyy-MM-dd') !== format(startEndCalendarClicked[1], 'yyyy-MM-dd') &&
        nextDay <= startEndCalendarClicked[1]
      ) {
        allDaysInRange.push(nextDay)
        nextDay = new Date(new Date(nextDay).setDate(new Date(nextDay).getDate() + 1))
      }
      allDaysInRange.push(startEndCalendarClicked[1])

      checkAvailability(allDaysInRange, startEndCalendarClicked[0], startEndCalendarClicked[1])
    }
  }

  const getAmenityReservations = async () => {
    try {
      const responseData = await API.getAmenityReservations(id, actualTzTimestamp, locationId)
      setAmenityReservations(responseData)
    } catch ({ message }) {
      console.error(message)
    }
  }

  const getDayStyling = (date) => {
    if (multiDayBooking) {
      const reservationsWithBookings = managerialBooking
        ? [
            ...amenityReservations,
            ...filteredManagerialBooking.map((booking) => {
              return { date: booking.startDate, endDate: booking.endDate }
            }),
          ]
        : amenityReservations

      const filteredReservations = reservationsWithBookings.filter((reservation) => {
        if (showAllDates) {
          return filteredManagerialBooking.some(
            (booking) =>
              (reservation.date === booking.startDate && reservation.endDate === booking.endDate) ||
              (reservation.date <= actualTzTimestamp && reservation.endDate >= actualTzTimestamp),
          )
        }

        return true
      })

      return isDayAlreadyReserved(date, filteredReservations, actualTzTimestamp, timezone) ===
        'startDay'
        ? 'reserved-start-day'
        : isDayAlreadyReserved(date, filteredReservations, actualTzTimestamp, timezone) === 'endDay'
          ? 'reserved-end-day'
          : isDayAlreadyReserved(date, filteredReservations, actualTzTimestamp, timezone) ===
              'fullDay'
            ? 'reserved-full-day'
            : null
    } else {
      return null
    }
  }

  const getDayDisabled = (date) => {
    const localDate = new Date(date)
    const hasUnavailableRegular =
      unavailableSlots &&
      unavailableSlots.regular.some((dateSlot) => {
        const startDate = getTimezonedDate(dateSlot.date, timezone)
        const endDate = getTimezonedDate(dateSlot.endDate, timezone)
        return (
          date >= startDate &&
          date <= endDate &&
          (!locationId || locationId === dateSlot.locationId)
        )
      })

    const hasUnavailableWeekly =
      unavailableSlots &&
      unavailableSlots.weekly.some((weeklySlot) => weeklySlot.date === new Date(localDate).getDay())
    const isDayDisabled = !daysAvailable.includes(localDate.getDay())

    if (isDayDisabled || hasUnavailableRegular) {
      return true
    }

    return multiDayBooking && hasUnavailableWeekly
  }

  const getMinimumDay = () => {
    const currentDay = fromZonedTime(new Date()).getTime()
    return fromZonedTime(currentDay > startDate ? currentDay : startDate, timezone)
  }

  const getMaximumDay = () => {
    if (!multiDayBooking) return endDate ? new Date(endDate) : undefined
    else return undefined
  }

  useEffect(() => {
    if (multiDayBooking) {
      getAmenityReservations()
    }
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  useEffect(() => {
    setHasConflicts(false)
    setSelectedStartEnd([])
  }, [showAllDates, blockMultipleDays])

  useEffect(() => {
    onChange({
      blockFullDay,
      blockMultipleDays,
      dateRange: selectedStartDate
        ? [
            selectedStartDate
              ? getTimezoneStamp(new Date(selectedStartDate).getTime(), { timezone })
              : undefined,
            selectedEndDate
              ? getTimezoneStamp(new Date(selectedEndDate).getTime(), { timezone })
              : undefined,
          ]
        : [],
      timeRange: selectedTimeRange ? [selectedTimeRange.startTime, selectedTimeRange.endTime] : [],
    })
  }, [
    selectedStartDate,
    selectedEndDate,
    selectedTimeRange,
    blockMultipleDays,
    blockFullDay,
    timezone,
  ])

  return (
    <React.Fragment>
      {!timeSelectViewVisible && (
        <div className="time-block-toggle">
          <ToggleField
            name="show-all-dates"
            labelOnRight
            onChange={() => setShowAllDates(!showAllDates)}
            value={showAllDates}
          />
          <span>
            {t('client-reservations:timeBlockCalendar.showAllDates')}
            <div data-tip data-for="show-all-dates-tip">
              <Icon icon="info" viewBox="0 0 25 25" />
              <ReactTooltip
                className="tooltip"
                id="show-all-dates-tip"
                multiline={true}
                place="top"
              >
                {t('client-reservations:timeBlockCalendar.showAllDatesTooltip')}
              </ReactTooltip>
            </div>
          </span>
        </div>
      )}
      {!multiDayBooking && !timeSelectViewVisible && (
        <div className="time-block-toggle">
          <ToggleField
            name="block-multiple-days"
            labelOnRight
            onChange={() => setBlockMultipleDays(!blockMultipleDays)}
            value={blockMultipleDays}
          />
          <span>{t('client-reservations:timeBlockCalendar.blockMultipleDays')}</span>
        </div>
      )}
      <div className="amenity-calendar">
        {timeSelectViewVisible && selectedStartEnd.length ? (
          <TimeSelectView
            amenity={amenity}
            selectedDate={selectedStartDate}
            onChange={setSelectedTimeRange}
            onBack={() => setIsTimeSelectVisible(false)}
            setHasConflicts={setHasConflicts}
            setReservedTimeRanges={setReservedTimeRanges}
            timezone={timezone}
            value={selectedTimeRange}
            showAllDates={showAllDates}
            locationId={locationId}
          />
        ) : (
          <div className="calendar-conatiner">
            <Calendar
              onChange={setSelectedDays}
              value={calendarValue}
              locale={'en'}
              calendarType={'US'}
              prev2Label={null}
              next2Label={null}
              minDate={getMinimumDay()}
              maxDate={getMaximumDay()}
              selectRange={multiDayBooking || blockMultipleDays}
              tileClassName={({ date }) => getDayStyling(date)}
              tileDisabled={({ date }) => getDayDisabled(date)}
              returnValue={multiDayBooking || blockMultipleDays ? 'range' : 'start'}
            />
          </div>
        )}
      </div>
      {/* {multiDayBooking && (
        <div className="reservations-modal-input-row">
          <div className="input-container inline">
            <CheckboxField
              name="blockEntireDay"
              value={blockFullDay}
              onChange={() => setBlockFullDay(!blockFullDay)}
              disabled={blockFullDayDisabled}
              label={'Block Entire Day'}
            />
          </div>
        </div>
      )} */}
    </React.Fragment>
  )
}

TimeBlockCalendar.propTypes = {
  amenity: PropTypes.object,
  building: PropTypes.object,
  timeSelectViewVisible: PropTypes.bool,
  selectedTimeRange: PropTypes.object,
  onChange: PropTypes.func,
  setIsTimeSelectVisible: PropTypes.func,
  setSelectedTimeRange: PropTypes.func,
  setHasConflicts: PropTypes.func,
  setReservedTimeRanges: PropTypes.func,
}

export default TimeBlockCalendar
